import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Nav, Spinner, Tab } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCapsules,
  faClipboardCheck,
  faExclamationTriangle,
  faHistory
} from "@fortawesome/free-solid-svg-icons";
import SubtleBadge from "components/common/SubtleBadge";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import useScheduleTokens, { shortLabelFor } from "./scheduleTokens";
import ScheduleBadge from "./ScheduleBadge";
import StockHistoryModal from "./StockHistoryModal";
import PatientAudit from "./PatientAudit";
import AuditHistory from "./AuditHistory";
import { errorMessage, getAuditChecklist, getAuditState } from "./auditApi";

/**
 * A patient's inventory, in three tabs:
 *   Medications      tracked medications with the quantity on record, in the
 *                    same searchable table as the patient list. Clicking a
 *                    medication opens its stock history.
 *   Audit checklist  count every tracked medication, add a photo and submit
 *                    (GET /inventory/audit/state/:id, GET /inventory/audit/checklist/:id,
 *                    POST /inventory/audit/image, POST /inventory/audit/submit/:id).
 *   Audit history    past audits (GET /inventory/audit/history/:id).
 * The two audit tabs load the first time they are opened.
 */

const formatQty = (n) => (Number.isInteger(n) ? String(n) : String(Number(Number(n).toFixed(2))));

// Header and cells share one alignment per column, like the patient list.
const ALIGN_LEFT = {
  headerProps: { className: "text-900 text-start" },
  cellProps: { className: "text-start" }
};
const ALIGN_CENTER = {
  headerProps: { className: "text-900 text-center" },
  cellProps: { className: "text-center" }
};

/* ------------------------------------------------------------------ */
/* Audit checklist tab                                                 */
/*                                                                     */
/* Looks up whether an audit is due, then shows the counting screen    */
/* (PatientAudit), which loads the checklist, uploads the photo and    */
/* submits the audit.                                                  */
/* ------------------------------------------------------------------ */

const AuditChecklistTab = ({ patientId, onSubmitted }) => {
  const [auditState, setAuditState] = useState(null);
  const [stateLoading, setStateLoading] = useState(true);
  const [stateError, setStateError] = useState("");
  const [ready, setReady] = useState(false);
  // Ignores a slow response that a newer refresh has already replaced.
  const requestRef = useRef(0);

  const loadState = useCallback(async () => {
    const requestId = ++requestRef.current;
    setStateLoading(true);
    setStateError("");
    try {
      const state = await getAuditState(patientId);
      if (requestId === requestRef.current) setAuditState(state);
    } catch (err) {
      // Not knowing the state never blocks counting — the audit can still be run.
      if (requestId === requestRef.current) {
        setAuditState(null);
        setStateError(errorMessage(err, "The audit status could not be loaded."));
      }
    } finally {
      if (requestId === requestRef.current) {
        setStateLoading(false);
        setReady(true);
      }
    }
  }, [patientId]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  if (!ready) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" role="status" />
        <p className="mt-3 text-600 fs-10 mb-0">Checking whether an audit is due...</p>
      </div>
    );
  }

  return (
    <>
      {stateError && (
        <Alert variant="warning" className="d-flex align-items-start border-0 rounded-3 fs-10 mb-2">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-3 mt-1 fs-9" />
          <div className="flex-1">
            <div className="fw-bold">We couldn&apos;t check whether an audit is due</div>
            <div>The due date below may be missing. You can still run an audit.</div>
            <div className="text-700 mt-1">Details: {stateError}</div>
          </div>
          <Button
            variant="outline-warning"
            size="sm"
            className="ms-3 rounded-pill px-3"
            onClick={loadState}
            disabled={stateLoading}
          >
            Retry
          </Button>
        </Alert>
      )}
      <PatientAudit
        patientId={patientId}
        auditState={auditState}
        stateLoading={stateLoading}
        onRefreshState={loadState}
        onSubmitted={onSubmitted}
      />
    </>
  );
};

AuditChecklistTab.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSubmitted: PropTypes.func
};

const PatientStock = ({ patientId }) => {
  const { tokens } = useScheduleTokens();
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  // Bumped after an audit is submitted so the history tab reloads.
  const [historyKey, setHistoryKey] = useState(0);

  const labelOf = useCallback(
    (token) => {
      const key = String(token).toLowerCase();
      const hit = tokens.find((t) => String(t.token).toLowerCase() === key);
      return hit ? hit.label : String(token);
    },
    [tokens]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { lines: rows } = await getAuditChecklist(patientId);
      setLines(rows);
    } catch (err) {
      setError(errorMessage(err, "The medication list could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  // A submitted audit can change what is on record, and belongs in the history.
  const afterAudit = useCallback(() => {
    load();
    setHistoryKey((k) => k + 1);
  }, [load]);

  const columns = useMemo(
    () => [
      {
        id: "medication",
        // Searchable by name, strength or order number.
        accessorFn: (l) => [l.drug_name, l.strength, l.pharmacy_order_id].filter(Boolean).join(" "),
        header: "Medication",
        meta: ALIGN_LEFT,
        cell: ({ row: { original: line } }) => (
          <>
            {line.pharmacy_order_id ? (
              // Same link styling as the patient names in the patient list.
              <a
                href="#!"
                role="button"
                className="fw-semibold text-decoration-none"
                onClick={(e) => {
                  e.preventDefault();
                  setSelected(line);
                }}
                title="View stock history"
              >
                {line.drug_name}
              </a>
            ) : (
              <span className="fw-semibold text-900" title="No order number, so no history to show">
                {line.drug_name}
              </span>
            )}
            {/* <div className="text-600 fs-11">
              {[line.strength, line.pharmacy_order_id ? `Rx ${line.pharmacy_order_id}` : ""]
                .filter(Boolean)
                .join(" · ")}
            </div> */}
          </>
        )
      },
      {
        id: "schedule",
        // Searchable by the short label, e.g. "C 2", "OTC", "Non Controlled".
        accessorFn: (l) => (l.schedule ? shortLabelFor(l.schedule) : ""),
        header: "Schedule",
        meta: ALIGN_LEFT,
        cell: ({ row: { original: line } }) =>
          line.schedule ? (
            <ScheduleBadge token={line.schedule} title={labelOf(line.schedule)} />
          ) : (
            <span className="text-500">—</span>
          )
      },
      {
        id: "onRecord",
        accessorFn: (l) => (l.no_balance_row ? -1 : Number(l.expected_qty)),
        header: "On record",
        meta: ALIGN_CENTER,
        cell: ({ row: { original: line } }) =>
          line.no_balance_row ? (
            <SubtleBadge bg="secondary" pill className="d-inline-block fs-11">
              No stock recorded
            </SubtleBadge>
          ) : (
            <span className="fw-semibold text-900">{formatQty(line.expected_qty)}</span>
          )
      }
    ],
    [labelOf]
  );

  const table = useAdvanceTable({
    data: lines,
    columns,
    selection: false,
    sortable: true,
    pagination: true
  });

  return (
    <Tab.Container defaultActiveKey="stock" mountOnEnter>
      <Nav variant="tabs" className="mb-3">
        <Nav.Item>
          <Nav.Link eventKey="stock" className="fw-semibold fs-10">
            <FontAwesomeIcon icon={faCapsules} className="me-2" />
            Medications
          </Nav.Link>
        </Nav.Item>
        {/* <Nav.Item>
          <Nav.Link eventKey="audit" className="fw-semibold fs-10">
            <FontAwesomeIcon icon={faClipboardCheck} className="me-2" />
            Audit checklist
          </Nav.Link>
        </Nav.Item> */}
        <Nav.Item>
          <Nav.Link eventKey="history" className="fw-semibold fs-10">
            <FontAwesomeIcon icon={faHistory} className="me-2" />
            Audit history
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <Tab.Content>
        <Tab.Pane eventKey="stock">
          <AdvanceTableProvider {...table}>
            <div className="d-flex gap-2 mb-3" style={{ maxWidth: 360 }}>
              <AdvanceTableSearchBox placeholder="Search medications..." />
            </div>

            {loading && lines.length === 0 ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" role="status" />
                <p className="mt-3 text-600 fs-10 mb-0">Loading medications...</p>
              </div>
            ) : error ? (
              <Alert variant="danger" className="d-flex align-items-start border-0 rounded-3 fs-10">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-3 mt-1 fs-9" />
                <div className="flex-1">
                  <div className="fw-bold">We couldn&apos;t load the medications</div>
                  <div>{error}</div>
                </div>
                <Button variant="outline-danger" size="sm" className="ms-3 rounded-pill px-3" onClick={load}>
                  Retry
                </Button>
              </Alert>
            ) : lines.length === 0 ? (
              <div className="text-center py-5 px-3 border border-300 border-dashed rounded-3">
                <FontAwesomeIcon icon={faCapsules} className="text-400 fs-5 mb-3" />
                <h6 className="text-900 fw-bold mb-1 fs-9">No tracked medications</h6>
                <p className="text-600 fs-10 mb-0">This patient has no medications being tracked.</p>
              </div>
            ) : (
              <>
                <AdvanceTable
                  headerClassName="bg-200 text-nowrap align-middle text-center"
                  rowClassName="align-middle"
                  tableProps={{ size: "lg", striped: true, className: "fs-10 mb-0" }}
                />
                <div className="mt-3">
                  <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
                </div>
              </>
            )}

            <StockHistoryModal
              patientId={patientId}
              line={selected}
              labelOf={labelOf}
              onHide={() => setSelected(null)}
              onAdjusted={load}
            />
          </AdvanceTableProvider>
        </Tab.Pane>

        <Tab.Pane eventKey="audit">
          <AuditChecklistTab patientId={patientId} onSubmitted={afterAudit} />
        </Tab.Pane>

        <Tab.Pane eventKey="history">
          <AuditHistory patientId={patientId} refreshKey={historyKey} />
        </Tab.Pane>
      </Tab.Content>
    </Tab.Container>
  );
};

PatientStock.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default PatientStock;
